import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChefHat, Award, Globe, UtensilsCrossed, Crown, ChevronLeft, ChevronRight, Sparkles, Star } from 'lucide-react';
export const CulinarySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const culinaryItems = [{
    name: 'Shalgam-Gosht',
    type: 'Royal Dish',
    description: 'Traditional turnip and meat stew (circa 1830s)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2zSbggEuZfU5JNU7W478R9/Gemini_Generated_Image_36dj2y36dj2y36dj.png',
    details: "A favorite in Lahore's royal kitchens during winter. Rich, aromatic with tender meat and sweet turnips, infused with warm spices and ghee."
  }, {
    name: 'Sher-e-Punjab Lassi',
    type: 'Royal Beverage',
    description: 'Lion of Punjab Smoothie',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
    details: 'Named after Maharaja Ranjit Singh. Rich dahi and milk blend with saffron and honey, topped with malai foam and crushed nuts.'
  }, {
    name: 'Paneer Musallam',
    type: 'Royal Dish',
    description: 'Whole stuffed cottage cheese in royal gravy (circa 1820s)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jKVXuEm1PxjzKySWjfAZhq/Gemini_Generated_Image_xc68qnxc68qnxc68.png',
    details: "Adapted for vegetarian courtiers in Maharaja Ranjit Singh's inclusive court. Creamy paneer with rich, aromatic gravy infused with saffron and cardamom."
  }, {
    name: "Hari Singh Nalwa's Tiger Fury",
    type: 'Royal Beverage',
    description: 'Fiery pomegranate and citrus cooler',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
    details: "Inspired by the legendary general. Bold pomegranate and citrus with tamarind, ginger, roasted cumin — fierce as Nalwa's spirit."
  }, {
    name: 'Adrakh dī Chhitt',
    type: 'Royal Dish',
    description: 'Silken ginger-milk broth (circa 1800s)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bhaBUE7vj4YCC3JJ4cMtPx/Gemini_Generated_Image_a0jilza0jilza0ji.png',
    details: 'Amritsar winter comfort with minimal masala, dairy warmth, and ginger heat. Satin-soft with pepper-lifted whispers of garlic.'
  }, {
    name: 'Chabeel of Compassion',
    type: 'Royal Beverage',
    description: 'Rose & Kewra Cooler',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
    details: 'Honoring the Sikh tradition of free service. Light rose-scented milk sherbet with kewra essence and floating rose petals.'
  }, {
    name: 'Āwle dī Sabzī',
    type: 'Royal Dish',
    description: 'Gooseberry in warm milk gravy (circa 1840s)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6xFqFVsJz8TbBJXY6napnV/Gemini_Generated_Image_izid72izid72izid.png',
    details: 'Pre-Partition home recipe transforming tart gooseberries through slow cooking. Sweet-tart balance with velvety milk and subtle spices.'
  }, {
    name: 'Shahi Aam Panna',
    type: 'Royal Beverage',
    description: 'Royal Raw Mango Punch',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
    details: "Courtly refinement of Punjab's aristocracy. Roasted green mangoes with jaggery, mint, lime, toasted cumin and black salt."
  }, {
    name: 'Darbār Dāl',
    type: 'Royal Dish',
    description: 'Black urad with fresh ginger juice (circa 1825)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iXyEit7wQgJsPqEtuf8wVy/Gemini_Generated_Image_afjvsuafjvsuafjv.png',
    details: 'Patiala court innovation with fresh ginger juice brightening slow-cooked lentils. Luxuriously creamy with earthy depth.'
  }, {
    name: "Pehalwan's Punch",
    type: 'Royal Beverage',
    description: 'Banana Jaggery Shake',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pMQJkiKcDmbaWHaNH1zaMn/Pehalwan%E2%80%99s_Punch_%28Banana_Jaggery_Shake%29.webp',
    details: 'Inspired by Punjabi wrestlers. Rich banana milkshake with yogurt, jaggery, cardamom and nutmeg, topped with honey and almonds.'
  }];
  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % culinaryItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoplay, culinaryItems.length]);
  const next = () => {
    setAutoplay(false);
    setCurrentIndex(prevIndex => (prevIndex + 1) % culinaryItems.length);
  };
  const prev = () => {
    setAutoplay(false);
    setCurrentIndex(prevIndex => (prevIndex - 1 + culinaryItems.length) % culinaryItems.length);
  };
  return <motion.section id="culinary" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1057">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1058"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1059">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1060">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1061">
            Culinary Heritage
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1062">
            A ROYAL
            <br data-id="element-1063" />
            <span className="text-amber-400" data-id="element-1064">FEAST</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1065"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1066">
            Journey through the opulent flavors of Maharaja Ranjit Singh's
            court, recreated by world-renowned culinary masters
          </p>
        </motion.div>
        {/* Chef Excellence Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-6xl mx-auto mb-24" data-id="element-1067">
          <div className="border border-amber-400/30 p-8 md:p-12 bg-black/40" data-id="element-1068">
            <div className="text-center mb-10" data-id="element-1069">
              <div className="inline-flex items-center gap-3 mb-6" data-id="element-1070">
                <ChefHat className="w-8 h-8 text-amber-400" data-id="element-1071" />
                <h3 className="font-serif text-3xl md:text-4xl text-white" data-id="element-1072">
                  World-Class{' '}
                  <span className="text-amber-400" data-id="element-1073">Culinary Masters</span>
                </h3>
              </div>
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-1074">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-1075" />)}
              </div>
              <p className="text-lg text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1076">
                Renowned chefs from around the globe unite to recreate the
                dining experience of Maharaja Ranjit Singh's court
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10" data-id="element-1077">
              <div className="text-center" data-id="element-1078">
                <Crown className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-1079" />
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1080">
                  Royal Court Cuisine
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1081">
                  Historically researched dishes from Punjab's nobility
                </p>
              </div>
              <div className="text-center" data-id="element-1082">
                <Globe className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-1083" />
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1084">
                  Global Excellence
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1085">
                  Master chefs from London, Toronto, Delhi, Singapore
                </p>
              </div>
              <div className="text-center" data-id="element-1086">
                <Award className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-1087" />
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1088">
                  Immersive Experience
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1089">
                  Each course synchronized with immersive projections
                </p>
              </div>
            </div>
            <div className="text-center pt-8 border-t border-white/10" data-id="element-1090">
              <p className="text-2xl text-amber-400 italic font-light mb-2" data-id="element-1091">
                "When you taste these dishes, you're tasting history itself."
              </p>
              <p className="text-white/60 font-light" data-id="element-1092">
                — Aiksimar Singh, Culinary Director
              </p>
            </div>
          </div>
        </motion.div>
        {/* Culinary Showcase */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-5xl mx-auto mb-20" data-id="element-1093">
          <div className="text-center mb-12" data-id="element-1094">
            <div className="inline-flex items-center gap-3 mb-4" data-id="element-1095">
              <UtensilsCrossed className="w-6 h-6 text-amber-400" data-id="element-1096" />
              <h3 className="font-serif text-3xl text-amber-400" data-id="element-1097">
                Royal Culinary Journey
              </h3>
            </div>
            <p className="text-white/60 font-light" data-id="element-1098">
              Flavors lost to time—now brought back to life
            </p>
          </div>
          <div className="relative" data-id="element-1099">
            <div className="border border-amber-400/30 overflow-hidden rounded-lg" data-id="element-1100">
              <AnimatePresence mode="wait" data-id="element-1101">
                <motion.div key={currentIndex} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.6
              }} data-id="element-1102">
                  <div className="aspect-[16/9] relative" data-id="element-1103">
                    <img src={culinaryItems[currentIndex].image} alt={culinaryItems[currentIndex].name} className="w-full h-full object-cover" data-id="element-1104" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" data-id="element-1105"></div>
                    <div className="absolute top-6 left-6" data-id="element-1106">
                      <div className="bg-amber-400/90 backdrop-blur-sm px-4 py-2 border border-white/20" data-id="element-1107">
                        <span className="text-black text-xs font-medium tracking-wider uppercase" data-id="element-1108">
                          {culinaryItems[currentIndex].type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 md:p-10 bg-black/80 space-y-4" data-id="element-1109">
                    <h4 className="font-serif text-3xl md:text-4xl text-amber-400" data-id="element-1110">
                      {culinaryItems[currentIndex].name}
                    </h4>
                    <p className="text-white/60 text-base font-light" data-id="element-1111">
                      {culinaryItems[currentIndex].description}
                    </p>
                    <div className="w-16 h-px bg-amber-400/30" data-id="element-1112"></div>
                    <p className="text-white/80 text-lg font-light leading-relaxed" data-id="element-1113">
                      {culinaryItems[currentIndex].details}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
              <button onClick={prev} className="absolute left-4 top-[30%] -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10" data-id="element-1114">
                <ChevronLeft className="w-6 h-6" data-id="element-1115" />
              </button>
              <button onClick={next} className="absolute right-4 top-[30%] -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10" data-id="element-1116">
                <ChevronRight className="w-6 h-6" data-id="element-1117" />
              </button>
            </div>
            <div className="flex justify-center mt-8 gap-2" data-id="element-1118">
              {culinaryItems.map((_, index) => <button key={index} onClick={() => {
              setAutoplay(false);
              setCurrentIndex(index);
            }} className={`h-px transition-all duration-300 ${index === currentIndex ? 'w-12 bg-amber-400' : 'w-8 bg-white/30 hover:bg-white/50'}`} data-id="element-1119" />)}
            </div>
          </div>
        </motion.div>
        {/* Closing Statement */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto" data-id="element-1120">
          <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-6" data-id="element-1121" />
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-1122">
            A Feast Fit for <span className="text-amber-400" data-id="element-1123">Royalty</span>
          </h3>
          <p className="text-xl text-white/70 font-light leading-relaxed" data-id="element-1124">
            Every dish tells a story. Every flavor carries centuries of
            tradition. Experience the culinary artistry that defined an empire.
          </p>
        </motion.div>
      </div>
    </motion.section>;
};